/** Shared atoms for the conversation screens. Deliberately the same badge
 *  vocabulary `envelope/PebbleEnvelopeView.tsx` established, so a tone means the
 *  same thing on every admin page. */
import type { ReactNode } from 'react';

export type Tone = 'green' | 'amber' | 'red' | 'blue' | 'purple' | 'gray';

const TONE_CLASS: Record<Tone, string> = {
  green: 'bg-green-100 text-green-800',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
  blue: 'bg-blue-100 text-blue-800',
  purple: 'bg-purple-100 text-purple-800',
  gray: 'bg-gray-100 text-gray-700',
};

export function Badge({ children, tone = 'gray', title }: { children: ReactNode; tone?: Tone; title?: string }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</div>
      <div className="text-sm text-gray-800">{children}</div>
    </div>
  );
}

/** A raw-JSON disclosure. Every synthesised view on these screens sits next to
 *  one: PD-1 passes rows through as stored precisely so an operator can diff the
 *  screen against `wrangler d1 execute` or a trigger payload, and a summary with
 *  no way back to the bytes breaks that. */
export function RawJson({ value, label = 'raw JSON', open = false }: { value: unknown; label?: string; open?: boolean }) {
  return (
    <details open={open} className="mt-2">
      <summary className="text-xs text-pebbl-600 hover:text-pebbl-800 cursor-pointer">{label}</summary>
      <pre className="mt-1 text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto max-h-80">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <span className="text-gray-400">{children}</span>;
}
